import { useCallback, useRef, useEffect } from 'react';
import type { UseApiStreamProps, UseApiStream } from './useApiStream.types';

const SSE_FRAME_DELIMITER = '\n\n';
const SSE_COMMENT_PREFIX = ':';

export const useApiStream = <TResponse, P>({ onMessage, onClose, onError, parser = JSON.parse }: UseApiStreamProps<TResponse>): UseApiStream<P> => {
  const activeControllersRef = useRef<Map<string, AbortController>>(new Map());

  const connect = useCallback(
    async (endpoint: string, params: P, requestId: string): Promise<boolean> => {
      const controller = new AbortController();
      activeControllersRef.current.set(requestId, controller);

      try {
        const response = await fetch(window.PMW.util.site_url(endpoint), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify(params),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) {
            onClose?.(requestId);
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Right now this is a logical part of parsing.
          const frames = buffer.split(SSE_FRAME_DELIMITER);
          buffer = frames.pop() ?? '';

          for (const frame of frames) {
            const parsedData = parseFrame<TResponse>(frame, parser);
            if (parsedData === undefined) {
              continue;
            }

            if (isCloseEvent(parsedData)) {
              onClose?.(requestId);
              return false;
            }

            onMessage(parsedData);
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          console.log(`Stream ${requestId} connection aborted by client.`);
          return false;
        } else {
          console.error('SSE (fetch) Error:', error);
          onError?.(error, requestId);
          return false;
        }
      } finally {
        if (activeControllersRef.current.get(requestId) === controller) {
          activeControllersRef.current.delete(requestId);
        }
      }
      return false;
    },
    [onMessage, onClose, onError, parser]
  );

  const disconnect = useCallback((requestId?: string) => {
    if (requestId) {
      const controller = activeControllersRef.current.get(requestId);

      if (controller) {
        controller.abort();
        activeControllersRef.current.delete(requestId);
      }

      console.log(`Stream disconnected client for: ${requestId}`);
      return;
    }

    activeControllersRef.current.forEach((controller, key) => {
      controller.abort();
      console.log(`Stream disconnected client for: ${key}`);
    });
    activeControllersRef.current.clear();
  }, []);

  useEffect(() => {
    return (): void => {
      disconnect();
    };
  }, [disconnect]);

  return { connect, disconnect };
};

function parseFrame<TResponse>(frame: string, parser: (raw: string) => TResponse): TResponse | undefined {
  const trimmed = frame.trim();
  if (trimmed === '' || trimmed.startsWith(SSE_COMMENT_PREFIX)) {
    return undefined;
  }

  try {
    return parser(trimmed);
  } catch (error) {
    console.error('SSE frame parse error:', trimmed, error);
    return undefined;
  }
}

function isCloseEvent(data: unknown): data is { event: string; type: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'event' in data &&
    'type' in data &&
    data.event === 'control' &&
    data.type === 'close'
  );
}
